import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import Select from "react-select";
import "../../css/Create.css";
import "../../css/Account.css"
import "../../css/ImportDocument.css";
import "../../css/FinalizeImportedDocument.css"
import "../../css/HomePage.css";
import "../../css/FinalizeImportedWorkspaceDocument.css";

function FinalizeImportedWorkspaceDocument({ onBack, documentTitle, selectedFile, imageAttachments = [], content, workspaceId, enhancedRightClickEnabled, showContextMenu, ContextMenuComponent }) {
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // Document type and status states
    const [selectedType, setSelectedType] = useState("");
    const [isPublic, setIsPublic] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [hasSpaces, setHasSpaces] = useState(false);

    // Content and metadata states
    const [name, setName] = useState(documentTitle || "");
    const [description, setDescription] = useState("");

    // Document relationship states
    const [allWorkspaceDocuments, setAllWorkspaceDocuments] = useState([]);
    const [selectedSpace, setSelectedSpace] = useState("");
    const [selectedParent, setSelectedParent] = useState("");

    // User and authentication states
    const [uid, setUid] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [workspaceDetails, setWorkspaceDetails] = useState(null);

    const navigate = useNavigate();

    // Authentication Effect
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUid(user.uid);
                setUserEmail(user.email);
            } else {
                navigate("/login");
            }
        });

        return () => {
            unsubscribe();
        };
    }, [navigate]);

    // User Data Fetching Effect
    useEffect(() => {
        if (uid && workspaceId) {
            fetchWorkspaceDetails();
            fetchWorkspaceDocuments();
        }
    }, [uid, workspaceId]);

    const fetchWorkspaceDetails = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                        "Requesting-User-Uid": uid,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setWorkspaceDetails(data);
            } else {
                console.error("Failed to fetch workspace details");
            }
        } catch (error) {
            console.error("Error fetching workspace details:", error);
        }
    };

    const fetchWorkspaceDocuments = async () => {
        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            // Fetch both spaces and pages from the workspace
            const [spacesResponse, pagesResponse] = await Promise.all([
                fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                            "Requesting-User-Uid": uid,
                        },
                    }
                ),
                fetch(
                    `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                            "Requesting-User-Uid": uid,
                        },
                    }
                )
            ]);

            const spacesData = spacesResponse.ok ? await spacesResponse.json() : [];
            const pagesData = pagesResponse.ok ? await pagesResponse.json() : [];

            // Process spaces data - handle both array and object responses
            const spaces = Array.isArray(spacesData) ? spacesData : (spacesData.spaces || []);
            const pages = Array.isArray(pagesData) ? pagesData : (pagesData.pages || []);

            // Combine spaces and pages for parent selection
            const allDocuments = [
                ...spaces.map(space => ({
                    ...space,
                    type: 'Space',
                    display_name: space.name
                })),
                ...pages.map(page => ({
                    ...page,
                    type: 'Page',
                    display_name: page.page_name
                }))
            ];

            setAllWorkspaceDocuments(allDocuments);
            
            // Check if workspace has any spaces for enabling page creation
            const hasExistingSpaces = spaces.length > 0;
            setHasSpaces(hasExistingSpaces);

        } catch (error) {
            console.error("Error fetching workspace documents:", error);
        }
    };

    const maxLengths = {
        Space: 100,
        Page: 150,
    };

    const currentMaxLength = maxLengths[selectedType] || 100;

    const handleToggle = (e) => {
        setIsPublic(e.target.checked);
    };

    const handleCompletedToggle = (e) => {
        setCompleted(e.target.checked);
    };

    const handleSelectedType = (e) => {
        setSelectedType(e.target.value);
        // Reset parent/space selection when type changes
        setSelectedSpace("");
        setSelectedParent("");
    };

    const handleSubmit = async () => {
        // Validation checks
        if (!selectedType) {
            alert("Please select a content type (Space or Page).");
            return;
        }

        if (!name || name.trim() === "") {
            alert("Please enter a name for your document.");
            return;
        }

        // Check that name only contains letters, numbers, spaces, periods, dashes, and underscores
        if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
            alert(
                "Name must contain only letters, numbers, spaces, periods, dashes, and underscores."
            );
            return;
        }

        if (selectedType === "Page" && !selectedSpace && !selectedParent) {
            alert("Please select a parent space or page for your new page.");
            return;
        }

        if (!content || content.trim() === "") {
            alert("Content cannot be empty. Please go back and ensure your document was imported correctly.");
            return;
        }

        // Set loading to true immediately after validation passes
        setIsLoading(true);

        try {
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;

            if (!token) {
                throw new Error("User is not authenticated.");
            }

            // Find parent type if this is a page
            let parentType = null;
            if (selectedType === "Page" && (selectedSpace || selectedParent)) {
                const parentId = selectedSpace || selectedParent;
                const parentDoc = allWorkspaceDocuments.find(doc => doc.id === parentId);
                if (parentDoc) {
                    parentType = parentDoc.type;
                }
            }

            // Extract mention UIDs from content and include for notifications
            const mentionedUids = (() => {
              const parser = new DOMParser();
              const parsed = parser.parseFromString(content, 'text/html');
              const spans = parsed.querySelectorAll('[data-user-uid]');
              const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
              return uids.filter(u => u !== uid);
            })();

            // Prepare the document data based on type
            const documentData = {
                // Use the correct field name based on document type
                ...(selectedType === "Page" ? { page_name: name } : { name: name }),
                description: description,
                is_public: isPublic,
                completed: completed,
                content: content,
                mentionedUids: mentionedUids,
            };

            // Add parent reference for pages based on type
            if (selectedType === "Page" && (selectedSpace || selectedParent)) {
                const parentId = selectedSpace || selectedParent;
                if (parentType === 'Space') {
                    documentData.space_id = parentId;
                } else if (parentType === 'Page') {
                    documentData.parent_page_id = parentId;
                }
            }

            // Select endpoint based on document type
            const endpoint =
                selectedType === "Space"
                    ? `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/spaces/create/`
                    : `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspaceId}/pages/create/`;

            // Make API request
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                    "Requesting-User-Uid": uid,
                },
                body: JSON.stringify(documentData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error || `Failed to create ${selectedType.toLowerCase()}`
                );
            }

            const responseData = await response.json();
            
            // Navigate to workspace after successful creation
            if (workspaceDetails?.custom_domain) {
                navigate(`/workspace/${workspaceDetails.custom_domain}`);
            } else {
                navigate(`/workspaces`);
            }

        } catch (error) {
            console.error(`Error creating ${selectedType.toLowerCase()}:`, error);
            alert(`Error creating ${selectedType.toLowerCase()}: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackClick = () => {
        if (onBack) {
            setIsTransitioning(true);
            setTimeout(() => {
                onBack();
            }, 150);
        }
    };

    if (!uid) {
        return (
            <div className="DocumentationMainPage__div">
                <div className="DocumentationMainPage__main-content">
                    <p className="large">It looks like you're not signed in.</p>
                    <p className="medium">
                        <strong>
                            To create a workspace document, you must have an account with us
                        </strong>
                        . Please log in to continue.
                    </p>
                    <button
                        onClick={() => navigate("/login")}
                        className="ConflictResolver__button"
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="DocumentationHomeComponent__loading-animation-div">
                <i className="fas fa-spinner fa-spin DocumentationHomeComponent__loading-animation-icon"></i>
            </div>
        );
    }

    // Filter documents for parent selection based on selected type
    const getParentOptions = () => {
        if (selectedType === "Page") {
            return allWorkspaceDocuments.map(doc => ({
                value: doc.type === 'Space' ? doc.id : doc.id,
                label: `${doc.display_name} (${doc.type})`,
                type: doc.type
            }));
        }
        return [];
    };

    return (
        <div
            className={`ImportDocument__main-div ${isTransitioning ? 'transitioning-back' : ''}`}
            onContextMenu={
                enhancedRightClickEnabled
                    ? (e) => {
                        e.preventDefault();
                        showContextMenu(e.clientX, e.clientY);
                    }
                    : undefined
            }
        >
            <div className="ImportDocument__content">
                <h2>Almost Done!</h2>
                
                <p className="medium" style={{ marginTop: "20px" }}>
                    Configure the settings for your imported workspace document.
                </p>

                <div style={{ marginTop: "20px", marginBottom: "20px" }}>
                    <p><strong>Workspace:</strong> {workspaceDetails?.name || 'Loading...'}</p>
                    <p><strong>Document Title:</strong> {documentTitle}</p>
                    <p><strong>File:</strong> {selectedFile?.name}</p>
                </div>

                {/* Document Type Selection */}
                <div className="Create__form-group">
                    <label className="Create__label">Content Type:</label>
                    <select
                        className="form-select Create__input"
                        aria-label="Default select example"
                        name="selected_type"
                        value={selectedType}
                        onChange={handleSelectedType}
                        required
                    >
                        <option value="">- Select Content Type -</option>
                        <option value="Space">Space</option>
                        <option value="Page" disabled={!hasSpaces}>
                            Page {!hasSpaces ? "(Create a Space first)" : ""}
                        </option>
                    </select>
                </div>

                {/* Document Name */}
                <div className="Create__form-group">
                    <label>
                        {selectedType === "Space" ? "Space" : "Page"} Name:
                    </label>
                    <input
                        className="Create__input-short-field"
                        type="text"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={currentMaxLength}
                        placeholder={`Max ${currentMaxLength} characters`}
                        required
                    />
                </div>

                {/* Parent Selection for Pages */}
                {selectedType === "Page" && hasSpaces && (
                    <div className="Create__form-group">
                        <label className="Create__label">Parent (Optional):</label>
                        <Select
                            value={
                                selectedSpace
                                    ? { value: selectedSpace, label: allWorkspaceDocuments.find(doc => doc.id === selectedSpace)?.display_name + " (Space)" }
                                    : selectedParent
                                    ? { value: selectedParent, label: allWorkspaceDocuments.find(doc => doc.id === selectedParent)?.display_name + " (Page)" }
                                    : null
                            }
                            onChange={(selectedOption) => {
                                if (selectedOption) {
                                    const selectedDoc = allWorkspaceDocuments.find(doc => doc.id === selectedOption.value);
                                    if (selectedDoc?.type === 'Space') {
                                        setSelectedSpace(selectedOption.value);
                                        setSelectedParent("");
                                    } else {
                                        setSelectedParent(selectedOption.value);
                                        setSelectedSpace("");
                                    }
                                } else {
                                    setSelectedSpace("");
                                    setSelectedParent("");
                                }
                            }}
                            options={getParentOptions()}
                            placeholder="Select a parent space or page..."
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                )}

                {/* Description */}
                <div className="Create__form-group">
                    <label>Description: </label>
                    <input
                        type="text"
                        className="Create__input-full-field"
                        name="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter a brief description..."
                        maxLength="500"
                    />
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        This description is what team members will see about this document.
                    </p>
                </div>

                {/* Completed Toggle */}
                <div className="Create__form-group">
                    <label>Publish this document upon submitting?</label>
                    <label className="Create__switch">
                        <input
                            type="checkbox"
                            checked={completed}
                            onChange={handleCompletedToggle}
                        />
                        <span className="Create__slider round"></span>
                    </label>
                </div>
                <div className="Create__form-group-helper-text">
                    <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                    <p className="Create__helper-text">
                        Whether the document is public or not, this document will not be <strong>viewable</strong> by anyone; including team members unless it's marked as completed. Team members with edit permission can still edit this document.
                    </p>
                </div>

                {/* Submit Button */}
                <button onClick={handleSubmit} className="FinalizeImportedWorkspaceDocument__submit-button" disabled={isLoading}>
                    {isLoading ? "Creating..." : `Create ${selectedType}`}
                </button>
            </div>

            {/* Back Arrow */}
            <div 
                className="ImportDocument__arrow ImportDocument__arrow--back" 
                onClick={handleBackClick}
                title="Go back"
                aria-label="Go back to previous step"
            >
                ❮
            </div>
            {enhancedRightClickEnabled && ContextMenuComponent}
        </div>
    );
}

export default FinalizeImportedWorkspaceDocument;
